import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { buildApiCandidates } from '../utils/apiUrl';

export interface EmpresaAutorizada {
  id: number;
  nombre: string;
  codigo?: string;
}

export interface LoginResult {
  usuario: {
    id: string;
    usuario_id?: string;
    username: string;
    nombre: string;
    email: string;
    empresa_id: string;
    labor: string;
    es_superusuario?: boolean;
  };
  access_token: string;
  refresh_token: string;
  empresas_autorizadas?: EmpresaAutorizada[];
}

interface LoginProps {
  onLogin: (result: LoginResult) => void;
}

const APP_FAVICON_DATA_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext y='78' font-size='80'%3E%F0%9F%8D%8D%3C/text%3E%3C/svg%3E";

function normalizeLoginPayload(data: any): LoginResult {
  const usuarioBase = data?.usuario || {};
  const empresasRaw = Array.isArray(data?.empresas_autorizadas) ? data.empresas_autorizadas : [];
  const empresas = empresasRaw
    .map((e: any) => ({
      id: Number(e?.id || 0),
      nombre: String(e?.nombre || ''),
      codigo: e?.codigo ? String(e.codigo) : undefined,
    }))
    .filter((e: EmpresaAutorizada) => e.id > 0 && e.nombre);

  const selectedEmpresaId = String(usuarioBase?.empresa_id || '').trim();
  const autoEmpresaId = selectedEmpresaId || (empresas.length === 1 ? String(empresas[0].id) : '');

  const accessToken = data?.access_token || data?.session?.access_token || '';
  const refreshToken = data?.refresh_token || data?.session?.refresh_token || '';

  return {
    usuario: {
      id: String(usuarioBase?.id || ''),
      usuario_id: usuarioBase?.usuario_id != null ? String(usuarioBase.usuario_id) : undefined,
      username: String(usuarioBase?.username || ''),
      nombre: String(usuarioBase?.nombre || usuarioBase?.username || 'Usuario'),
      email: String(usuarioBase?.email || ''),
      empresa_id: autoEmpresaId,
      labor: String(usuarioBase?.labor || 'Colaborador'),
      es_superusuario: Boolean(usuarioBase?.es_superusuario),
    },
    access_token: String(accessToken),
    refresh_token: String(refreshToken),
    empresas_autorizadas: empresas,
  };
}

const SLIDES = [
  {
    title: 'Control total de su operacion agricola, lote por lote.',
    subtitle: 'Registre aplicaciones, valide dosis y sincronice con ERP en tiempo real.',
    image: `${process.env.PUBLIC_URL}/branding/login-1.jpg`,
    bg: '#0f2a1a',
  },
  {
    title: 'Ingreso por usuario con permisos por empresa.',
    subtitle: 'Misma seguridad y reglas de acceso del ERP principal.',
    image: `${process.env.PUBLIC_URL}/branding/login-2.JPG`,
    bg: '#0b2040',
  },
  {
    title: 'Experiencia visual unificada en todo MYA.',
    subtitle: 'Branding consistente para ERP, Empacadora y Aplicaciones.',
    image: `${process.env.PUBLIC_URL}/branding/login-3.jpg`,
    bg: '#1a1a2e',
  },
];

async function loginViaApi(username: string, password: string, moduloCodigo?: string, empresaId?: number) {
  const endpointCandidates = buildApiCandidates('/auth/login');

  let unavailableReason = '';
  let unavailableEndpoint = endpointCandidates[0];

  for (const endpoint of endpointCandidates) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          ...(moduloCodigo ? { modulo_codigo: moduloCodigo } : {}),
          ...(empresaId ? { empresa_id: empresaId } : {}),
        }),
      });
      const contentType = String(res.headers.get('content-type') || '').toLowerCase();
      const data = await res.json().catch(() => ({}));

      // Si retorna HTML u otra respuesta no-API, marcar como indisponible.
      const looksLikeNonApiResponse =
        !contentType.includes('application/json') ||
        (res.ok && (!data || typeof data !== 'object' || data.ok === undefined));
      if (looksLikeNonApiResponse) {
        unavailableEndpoint = endpoint;
        unavailableReason = `non-json response (status ${res.status})`;
        continue;
      }

      if (!res.ok || !data?.ok) {
        return {
          kind: 'error' as const,
          message: String(data?.message || 'Usuario o contrasena incorrectos'),
        };
      }
      const normalized = normalizeLoginPayload(data);
      if (!normalized.access_token || !normalized.refresh_token || !normalized.usuario.username) {
        return {
          kind: 'error' as const,
          message: 'Respuesta de autenticacion invalida del servidor',
        };
      }
      return { kind: 'success' as const, data: normalized };
    } catch (err: any) {
      unavailableEndpoint = endpoint;
      unavailableReason = String(err?.message || err || 'fetch failed');
    }
  }

  return {
    kind: 'unavailable' as const,
    endpoint: unavailableEndpoint,
    reason: unavailableReason || 'fetch failed',
  };
}

async function loginWithRetry(
  username: string,
  password: string,
  moduloCodigo: string,
  empresaId?: number
) {
  let result = await loginViaApi(username, password, moduloCodigo, empresaId);
  if (result.kind !== 'success') {
    await new Promise((r) => setTimeout(r, 1500));
    result = await loginViaApi(username, password, moduloCodigo, empresaId);
  }
  if (result.kind !== 'success') {
    await new Promise((r) => setTimeout(r, 2500));
    result = await loginViaApi(username, password, moduloCodigo, empresaId);
  }
  return result;
}

export function Login({ onLogin }: LoginProps) {
  const moduloCodigo = 'pina';
  const [slideIndex, setSlideIndex] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [recoveryMsg, setRecoveryMsg] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaAutorizada[]>([]);
  const [pendingLogin, setPendingLogin] = useState<LoginResult | null>(null);

  useEffect(() => {
    const t = setInterval(() => setSlideIndex((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // En iOS Safari (y algunos navegadores móviles), cuando el teclado
    // autocompleta un campo, a veces no dispara el evento que React escucha
    // — el estado (username/password) queda desactualizado aunque en
    // pantalla se vea el valor correcto, y se termina enviando otra cosa.
    // Se lee el valor real del DOM al momento de enviar como fuente de
    // verdad, con el estado de React como respaldo.
    const formData = new FormData(e.currentTarget);
    const usuarioFinal = String(formData.get('username') ?? username).trim().toUpperCase();
    const passwordFinal = String(formData.get('password') ?? password).trim();

    if (!usuarioFinal || !passwordFinal) {
      setError('Complete usuario y contrasena');
      return;
    }

    setCargando(true);
    setError('');
    setEmpresas([]);
    setPendingLogin(null);

    const result = await loginWithRetry(usuarioFinal, passwordFinal, moduloCodigo);

    if (result.kind === 'success') {
      const data = result.data;
      if (data.usuario.empresa_id) {
        onLogin(data);
      } else if ((data.empresas_autorizadas || []).length > 1) {
        setEmpresas(data.empresas_autorizadas || []);
        setPendingLogin(data);
      } else {
        setError('No fue posible determinar la empresa para este usuario.');
      }
    } else if (result.kind === 'error') {
      setError(result.message);
    } else {
      const endpoint = (result as any)?.endpoint ? ` (${(result as any).endpoint})` : '';
      const reason = (result as any)?.reason ? ` Detalle: ${(result as any).reason}` : '';
      setError(`Servidor no disponible. Intente en unos segundos.${endpoint}${reason}`);
    }

    setCargando(false);
  };

  const selectEmpresa = async (empresaId: number) => {
    if (!pendingLogin) return;

    setError('');
    onLogin({
      ...pendingLogin,
      usuario: {
        ...pendingLogin.usuario,
        empresa_id: String(empresaId),
      },
    });
  };

  const solicitarRecuperacion = async () => {
    const identifier = username.trim();
    if (!identifier) {
      setRecoveryMsg('Digite su usuario o correo para enviar la recuperacion.');
      return;
    }

    setRecovering(true);
    setRecoveryMsg('');
    setError('');
    const endpoints = buildApiCandidates('/auth/request-password-reset');
    let sent = false;

    for (const endpoint of endpoints) {
      try {
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            identifier,
            redirect_to: `${window.location.origin}${window.location.pathname}`,
          }),
        });
        if (resp.ok) {
          sent = true;
          break;
        }
      } catch {
        // probar siguiente candidato
      }
    }

    setRecovering(false);
    setRecoveryMsg(
      sent
        ? 'Si la cuenta existe, recibira instrucciones en su correo.'
        : 'No se pudo solicitar la recuperacion. Revise que el ERP este activo.'
    );
  };

  const slide = SLIDES[slideIndex];

  return (
    <div className="login-wrap">
      <section className="login-showcase">
        {SLIDES.map((s, i) => (
          <div
            key={i}
            className={`login-slide-layer ${i === slideIndex ? 'active' : ''}`}
            style={{ backgroundColor: s.bg, backgroundImage: `url(${s.image})` }}
          />
        ))}
        <div className="login-deco" />
        <div className="login-corner-brand" aria-hidden="true">
          <img src={APP_FAVICON_DATA_URI} alt="" />
          <span>Sistema MYA</span>
        </div>
        <div className="login-brand">
          <span className="login-badge">APLICACIONES | SISTEMAS MYA</span>
          <h1 className="login-hero-title">{slide.title}</h1>
          <p className="login-hero-sub">{slide.subtitle}</p>
          <span className="login-hero-chip">Soporte | +506 8379 0976</span>
          <div className="login-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`login-dot ${i === slideIndex ? 'active' : ''}`}
                onClick={() => setSlideIndex(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="login-panel-wrap">
        <div className="login-panel">
          <div className="login-logo">
            <img
              src={`${process.env.PUBLIC_URL}/branding/login-logo-pina.png`}
              alt="Logo"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                img.onerror = null;
                img.src = APP_FAVICON_DATA_URI;
              }}
            />
          </div>
          <div className="login-title">Iniciar Sesion</div>
          <div className="login-sub">Morales y Alfaro - Aplicaciones de Campo</div>

          {empresas.length > 1 ? (
            <div>
              <p className="field-label" style={{ marginBottom: '10px' }}>
                Seleccione la empresa de Control de Pina
              </p>

              <div style={{ display: 'grid', gap: '8px' }}>
                {empresas.map((empresa) => (
                  <button
                    key={empresa.id}
                    type="button"
                    className="btn-login"
                    style={{ textAlign: 'left', justifyContent: 'flex-start', display: 'flex', gap: '8px', alignItems: 'center' }}
                    onClick={() => selectEmpresa(empresa.id)}
                    disabled={cargando}
                  >
                    <span>{empresa.nombre}</span>
                    {empresa.codigo ? <span style={{ opacity: 0.75, fontFamily: 'monospace' }}>({empresa.codigo})</span> : null}
                  </button>
                ))}
              </div>

              {error && <div className="login-error">{error}</div>}

              <button
                className="btn-login"
                type="button"
                style={{ marginTop: '10px', background: '#374151' }}
                onClick={() => {
                  setEmpresas([]);
                  setPendingLogin(null);
                  setError('');
                }}
                disabled={cargando}
              >
                Volver
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="field-label">Usuario</label>
              <input
                className="field-input"
                type="text"
                name="username"
                placeholder="Ingrese su usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value.toUpperCase())}
                autoFocus
                autoComplete="username"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('app-password')?.focus();
                  }
                }}
              />

              <label className="field-label">Contrasena</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="app-password"
                  className="field-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ paddingRight: '42px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                  aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                  style={{
                    position: 'absolute', right: '10px', top: '22px', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    display: 'flex', alignItems: 'center', color: '#64748b',
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {error && <div className="login-error">{error}</div>}
              {recoveryMsg && (
                <div className="login-error" style={{ color: '#cbd5e1' }}>
                  {recoveryMsg}
                </div>
              )}

              <button className="btn-login" type="submit" disabled={cargando}>
                {cargando ? 'Verificando...' : 'Continuar ->'}
              </button>
              <button
                type="button"
                className="btn-login"
                style={{ marginTop: '8px', background: '#334155' }}
                onClick={solicitarRecuperacion}
                disabled={recovering || cargando}
              >
                {recovering ? 'Enviando...' : '¿Olvidó su contraseña?'}
              </button>
            </form>
          )}

          <div className="login-footer">
            Sistema MYA - Aplicaciones v1.0 - {new Date().getFullYear()}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Login;

